<template>
  <NuxtLink v-bind="linkProps">
    <slot />
  </NuxtLink>
</template>

<script setup lang="ts">
import { linkDetails } from '~/composables/link'
import { useLinkRewrites } from '~/composables/craft'

interface Props {
  url?: object | string
  target?: '_self' | '_blank' | '_parent' | '_top'
  rel?: string
}

const props = defineProps<Props>()

const rewrites = useLinkRewrites()
// Resolve the current host: prefer the SSR request header on the server,
// otherwise read it from `window` (Nuxt client or any browser-like environment).
const currentHost = `//${(import.meta.server && useRequestHeader('host')) || window?.location?.host || ''}`
const localePath = useLocalePath()

const linkProps = computed(() => {
  const targetUrl = typeof props.url === 'object' ? localePath(props.url) : props.url

  const { isExternal, url } = linkDetails(currentHost, targetUrl, { rewrites })

  // Resolve target: honor an explicit prop, otherwise no value (browser default).
  const target = props.target || null

  // Default state for every link: `noRel: true` so NuxtLink does not auto-emit a rel.
  // The only case where noRel is dropped — and a `rel` is set instead — is a truly
  // external link (different registered domain). This keeps the security boundary
  // tight and independent of the target (same-tab vs new-tab).
  const relProps = isExternal ? { rel: props.rel || 'noopener' } : { noRel: true }

  return {
    to: url,
    ...(target ? { target } : {}),
    ...relProps,
  }
})
</script>
